import * as React from "react";
import useEmblaCarousel, { type UseEmblaCarouselType } from "embla-carousel-react";
import { ArrowLeft, ArrowRight, Pause, Play } from "lucide-react";
import { cn } from "./cn";
import { Button } from "./button";
import { controlsFitClasses, prefersReducedMotion, slideLabel } from "./motion-logic";

/**
 * Carousel — the shadcn/ui carousel (Embla underneath), re-mapped to our tokens.
 *
 * Divergences from stock shadcn, all additive:
 * - prev/next honour reduced motion (they jump instead of gliding; the
 *   theme.css rule can't reach motion Embla drives from script);
 * - prev/next default to 44px (h-11 w-11) touch targets.
 *
 * Most pages want CardCarousel (below), which wires the responsive card
 * widths, controls and labels; the primitives are here for anything else.
 */

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type CarouselProps = {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: "horizontal" | "vertical";
  setApi?: (api: CarouselApi) => void;
};

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
} & CarouselProps;

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

function useCarousel() {
  const context = React.useContext(CarouselContext);
  if (!context) throw new Error("useCarousel must be used within a <Carousel />");
  return context;
}

const Carousel = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & CarouselProps
>(({ orientation = "horizontal", opts, setApi, plugins, className, children, ...props }, ref) => {
  const [carouselRef, api] = useEmblaCarousel(
    { ...opts, axis: orientation === "horizontal" ? "x" : "y" },
    plugins,
  );
  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);

  const onSelect = React.useCallback((api: CarouselApi) => {
    if (!api) return;
    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
  }, []);

  const scrollPrev = React.useCallback(() => {
    api?.scrollPrev(prefersReducedMotion());
  }, [api]);

  const scrollNext = React.useCallback(() => {
    api?.scrollNext(prefersReducedMotion());
  }, [api]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const prevKey = orientation === "horizontal" ? "ArrowLeft" : "ArrowUp";
      const nextKey = orientation === "horizontal" ? "ArrowRight" : "ArrowDown";
      // Leave arrow keys alone inside text fields and other widgets that use them.
      const target = event.target as HTMLElement;
      if (target.closest("input, textarea, select, [contenteditable='true'], [role='slider']")) {
        return;
      }
      if (event.key === prevKey) {
        event.preventDefault();
        scrollPrev();
      } else if (event.key === nextKey) {
        event.preventDefault();
        scrollNext();
      }
    },
    [orientation, scrollPrev, scrollNext],
  );

  React.useEffect(() => {
    if (!api || !setApi) return;
    setApi(api);
  }, [api, setApi]);

  React.useEffect(() => {
    if (!api) return;
    onSelect(api);
    api.on("reInit", onSelect);
    api.on("select", onSelect);
    return () => {
      api.off("reInit", onSelect);
      api.off("select", onSelect);
    };
  }, [api, onSelect]);

  return (
    <CarouselContext.Provider
      value={{
        carouselRef,
        api,
        opts,
        orientation: orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
      }}
    >
      <div
        ref={ref}
        onKeyDownCapture={handleKeyDown}
        className={cn("relative", className)}
        role="region"
        aria-roledescription="carousel"
        {...props}
      >
        {children}
      </div>
    </CarouselContext.Provider>
  );
});
Carousel.displayName = "Carousel";

const CarouselContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const { carouselRef, orientation } = useCarousel();
    return (
      <div ref={carouselRef} className="overflow-hidden">
        <div
          ref={ref}
          className={cn("flex", orientation === "horizontal" ? "-ml-4" : "-mt-4 flex-col", className)}
          {...props}
        />
      </div>
    );
  },
);
CarouselContent.displayName = "CarouselContent";

const CarouselItem = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const { orientation } = useCarousel();
    return (
      <div
        ref={ref}
        role="group"
        aria-roledescription="slide"
        className={cn(
          "min-w-0 shrink-0 grow-0 basis-full",
          orientation === "horizontal" ? "pl-4" : "pt-4",
          className,
        )}
        {...props}
      />
    );
  },
);
CarouselItem.displayName = "CarouselItem";

const CarouselPrevious = React.forwardRef<HTMLButtonElement, React.ComponentProps<typeof Button>>(
  ({ className, variant = "outline", size = "icon", ...props }, ref) => {
    const { orientation, scrollPrev, canScrollPrev } = useCarousel();
    return (
      <Button
        ref={ref}
        variant={variant}
        size={size}
        className={cn(
          "absolute h-11 w-11 rounded-full bg-card",
          orientation === "horizontal"
            ? "-left-12 top-1/2 -translate-y-1/2"
            : "-top-12 left-1/2 -translate-x-1/2 rotate-90",
          className,
        )}
        disabled={!canScrollPrev}
        onClick={scrollPrev}
        aria-label="Previous slide"
        {...props}
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
      </Button>
    );
  },
);
CarouselPrevious.displayName = "CarouselPrevious";

const CarouselNext = React.forwardRef<HTMLButtonElement, React.ComponentProps<typeof Button>>(
  ({ className, variant = "outline", size = "icon", ...props }, ref) => {
    const { orientation, scrollNext, canScrollNext } = useCarousel();
    return (
      <Button
        ref={ref}
        variant={variant}
        size={size}
        className={cn(
          "absolute h-11 w-11 rounded-full bg-card",
          orientation === "horizontal"
            ? "-right-12 top-1/2 -translate-y-1/2"
            : "-bottom-12 left-1/2 -translate-x-1/2 rotate-90",
          className,
        )}
        disabled={!canScrollNext}
        onClick={scrollNext}
        aria-label="Next slide"
        {...props}
      >
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </Button>
    );
  },
);
CarouselNext.displayName = "CarouselNext";

/* ── CardCarousel ─────────────────────────────────────────────────────────── */

export interface CardCarouselProps<T> {
  items: T[];
  renderItem: (item: T, index: number) => React.ReactNode;
  getKey: (item: T) => string;
  /** Accessible name for the carousel, e.g. "Upcoming events". */
  label: string;
  /** Per-card classes; pass `basis-*` classes to change how many show at once. */
  itemClassName?: string;
  className?: string;
  /** Shown instead of the carousel when there are no items. */
  emptyState?: React.ReactNode;
  /**
   * Advance on its own: `true` (default) = every 5s, a number = that many ms,
   * `false` = never. Always off under prefers-reduced-motion.
   */
  autoplay?: boolean | number;
}

const AUTOPLAY_MS = 5000;
/** After a swipe/click/key, wait this long before advancing again. */
const RESUME_AFTER_MS = 8000;

/**
 * Autoplay the way modern carousels behave (and WCAG 2.2.2 asks): advances on
 * a timer and loops, but never while someone is looking at or using it —
 * paused on hover, while focus is inside, while the tab is hidden or the
 * carousel is off-screen, and for a while after any swipe/click/key. The
 * visitor can pause it outright with the pause button.
 */
function useAutoplay(
  api: CarouselApi,
  root: React.RefObject<HTMLDivElement | null>,
  delay: number | null,
) {
  const [paused, setPaused] = React.useState(false); // the visitor's choice
  const hover = React.useRef(false);
  const focus = React.useRef(false);
  const visible = React.useRef(true);
  const idleUntil = React.useRef(0);

  React.useEffect(() => {
    if (!api || delay === null || paused) return;
    const el = root.current;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const blocked = () =>
      hover.current || focus.current || !visible.current || document.hidden || Date.now() < idleUntil.current;
    const tick = () => {
      if (!blocked()) {
        if (api.canScrollNext()) api.scrollNext();
        else api.scrollTo(0);
      }
      timer = setTimeout(tick, Math.max(delay, idleUntil.current - Date.now()));
    };
    timer = setTimeout(tick, delay);

    const interacted = () => {
      idleUntil.current = Date.now() + RESUME_AFTER_MS;
    };
    const onEnter = () => (hover.current = true);
    const onLeave = () => (hover.current = false);
    const onFocusIn = () => (focus.current = true);
    const onFocusOut = (e: FocusEvent) => {
      if (!el?.contains(e.relatedTarget as Node | null)) focus.current = false;
    };
    api.on("pointerDown", interacted);
    el?.addEventListener("pointerenter", onEnter);
    el?.addEventListener("pointerleave", onLeave);
    el?.addEventListener("focusin", onFocusIn);
    el?.addEventListener("focusout", onFocusOut);
    el?.addEventListener("keydown", interacted);
    el?.addEventListener("click", interacted);
    const io =
      el && "IntersectionObserver" in window
        ? new IntersectionObserver(([e]) => (visible.current = !!e?.isIntersecting), { threshold: 0.25 })
        : null;
    if (el && io) io.observe(el);
    return () => {
      clearTimeout(timer);
      api.off("pointerDown", interacted);
      el?.removeEventListener("pointerenter", onEnter);
      el?.removeEventListener("pointerleave", onLeave);
      el?.removeEventListener("focusin", onFocusIn);
      el?.removeEventListener("focusout", onFocusOut);
      el?.removeEventListener("keydown", interacted);
      el?.removeEventListener("click", interacted);
      io?.disconnect();
    };
  }, [api, delay, paused, root]);

  return { paused, setPaused };
}

const DEFAULT_ITEM_BASIS = "basis-[82%] sm:basis-1/2 lg:basis-1/3";

/**
 * A row of cards that swipes. Phones see one card at ~82% so the next one
 * peeks in (the cue that there is more); 2 across from `sm`, 3 from `lg`.
 * Swipe or drag, arrow keys once focus is inside, or the 44px prev/next
 * buttons, and position dots. Autoplays and loops by default (see
 * useAutoplay — pauses for hover/focus/interaction, has a pause button, off
 * under reduced motion); with `autoplay={false}` it stops at either end.
 * Controls are hidden entirely when every card already fits.
 */
function CardCarousel<T>({
  items,
  renderItem,
  getKey,
  label,
  itemClassName,
  className,
  emptyState,
  autoplay = true,
}: CardCarouselProps<T>) {
  const [api, setApi] = React.useState<CarouselApi>();
  const rootRef = React.useRef<HTMLDivElement>(null);
  const [reduced, setReduced] = React.useState(true); // decided after mount
  React.useEffect(() => setReduced(prefersReducedMotion()), []);
  const delay = autoplay === false || reduced ? null : typeof autoplay === "number" ? autoplay : AUTOPLAY_MS;
  const { paused, setPaused } = useAutoplay(api, rootRef, delay);
  const [snaps, setSnaps] = React.useState<number[]>([]);
  const [selected, setSelected] = React.useState(0);
  // null until Embla has measured; CSS (controlsFitClasses) decides until then.
  const [overflows, setOverflows] = React.useState<boolean | null>(null);

  React.useEffect(() => {
    if (!api) return;
    const sync = () => {
      setOverflows(api.canScrollPrev() || api.canScrollNext());
      setSnaps(api.scrollSnapList());
      setSelected(api.selectedScrollSnap());
    };
    sync();
    api.on("reInit", sync);
    api.on("select", sync);
    api.on("resize", sync);
    return () => {
      api.off("reInit", sync);
      api.off("select", sync);
      api.off("resize", sync);
    };
  }, [api]);

  if (items.length === 0) return <>{emptyState ?? null}</>;

  const total = items.length;
  const controlsClass =
    overflows === null
      ? itemClassName
        ? ""
        : controlsFitClasses(total)
      : overflows
        ? ""
        : "hidden";

  return (
    <Carousel
      ref={rootRef}
      setApi={setApi}
      opts={{ align: "start", loop: delay !== null }}
      aria-label={label}
      className={className}
    >
      <CarouselContent>
        {items.map((item, index) => (
          <CarouselItem
            key={getKey(item)}
            aria-label={slideLabel(index, total)}
            className={cn(DEFAULT_ITEM_BASIS, itemClassName)}
          >
            {renderItem(item, index)}
          </CarouselItem>
        ))}
      </CarouselContent>
      <div className={cn("mt-4 flex items-center gap-2", controlsClass)}>
        {snaps.length > 1 && (
          <div className="mr-auto flex flex-wrap items-center" role="group" aria-label={`${label}: choose a position`}>
            {snaps.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => api?.scrollTo(i, prefersReducedMotion())}
                aria-label={`Go to ${i + 1} of ${snaps.length}`}
                aria-current={i === selected ? "true" : undefined}
                className="group flex h-6 w-6 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span
                  aria-hidden
                  className={cn(
                    "block h-2 rounded-full transition-all duration-300",
                    i === selected ? "w-5 bg-primary" : "w-2 bg-border group-hover:bg-muted",
                  )}
                />
              </button>
            ))}
          </div>
        )}
        {delay !== null && (
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-11 w-11 rounded-full bg-card"
            onClick={() => setPaused(!paused)}
            aria-label={paused ? "Play slideshow" : "Pause slideshow"}
            aria-pressed={paused}
          >
            {paused ? <Play className="h-4 w-4" aria-hidden="true" /> : <Pause className="h-4 w-4" aria-hidden="true" />}
          </Button>
        )}
        <CarouselPrevious className="static translate-x-0 translate-y-0" />
        <CarouselNext className="static translate-x-0 translate-y-0" />
      </div>
    </Carousel>
  );
}

export {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  CardCarousel,
  useCarousel,
};
